import Button from 'devextreme-react/button';
import Drawer from 'devextreme-react/drawer';
import ScrollView from 'devextreme-react/scroll-view';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header, SideNavigationMenu, Footer } from '../../components';
import './side-nav-inner-toolbar.scss';
import { sizes, subscribe, unsubscribe } from '../../utils/media-query';
import { Template } from 'devextreme-react/core/template';
import { menuPreInitPatch } from '../../utils/patches';

function SideNavInnerToolbar(props) {
    const { menuItems, userMenuItems, children } = props;
    const navigate = useNavigate();
    const location = useLocation();

    const getDrawerConfig = useCallback(() => {
        const isXSmall = sizes()['screen-x-small'];
        const isLarge = sizes()['screen-large'];

        return {
            menuMode: isLarge ? 'shrink' : 'overlap',
            menuRevealMode: isXSmall ? 'slide' : 'expand',
            minMenuSize: isXSmall ? 0 : 60,
            shaderEnabled: !isLarge
        };
    }, []);

    const [menuOpened, setMenuOpened] = useState(sizes()['screen-large']);
    const [temporaryMenuOpened, setTemporaryMenuOpened] = useState(false);
    const [drawerConfig, setDrawerConfig] = useState(getDrawerConfig());

    const { menuMode, menuRevealMode, minMenuSize, shaderEnabled } = drawerConfig;

    // Initialize menuPatch once with a mock component context
    const menuPatchRef = useRef(null);
    const stateRef = useRef({ menuOpened, temporaryMenuOpened, ...drawerConfig });

    // Update stateRef when state changes
    stateRef.current = { menuOpened, temporaryMenuOpened, ...drawerConfig };

    if (!menuPatchRef.current) {
        const mockComponent = {
            get state() {
                return stateRef.current;
            },
            setState: (updates) => {
                if (typeof updates === 'function') {
                    const newState = updates(stateRef.current);
                    Object.keys(newState).forEach(key => {
                        if (key === 'menuOpened') setMenuOpened(newState[key]);
                        if (key === 'temporaryMenuOpened') setTemporaryMenuOpened(newState[key]);
                    });
                } else {
                    if (updates.menuOpened !== undefined) setMenuOpened(updates.menuOpened);
                    if (updates.temporaryMenuOpened !== undefined) setTemporaryMenuOpened(updates.temporaryMenuOpened);
                }
            }
        };
        menuPatchRef.current = menuPreInitPatch(mockComponent);
    }
    const menuPatch = menuPatchRef.current;

    const updateDrawer = useCallback(() => {
        setDrawerConfig(getDrawerConfig());
    }, [getDrawerConfig]);

    useEffect(() => {
        subscribe(updateDrawer);
        return () => {
            unsubscribe(updateDrawer);
        };
    }, [updateDrawer]);

    const closeDrawer = useCallback(() => {
        if (!shaderEnabled) {
            return false;
        }
        setMenuOpened(false);
        return true;
    }, [shaderEnabled]);

    const toggleMenu = useCallback(({ event }) => {
        setMenuOpened(prev => !prev);
        event.stopPropagation();
    }, []);

    const hideMenuAfterNavigation = useMemo(() => {
        return menuMode === 'overlap' || temporaryMenuOpened;
    }, [menuMode, temporaryMenuOpened]);

    const navigationChanged = useCallback((event) => {
        const path = event.itemData.path;
        const pointerEvent = event.event;

        if (path && menuOpened) {
            if (event.node.selected) {
                pointerEvent.preventDefault();
            } else {
                navigate(path);
            }

            if (hideMenuAfterNavigation) {
                setMenuOpened(false);
                setTemporaryMenuOpened(false);
                pointerEvent.stopPropagation();
            }
        } else {
            pointerEvent.preventDefault();
        }
    }, [menuOpened, navigate, hideMenuAfterNavigation]);

    const navigationClick = useCallback(() => {
        setMenuOpened(prev => {
            if (!prev) {
                setTemporaryMenuOpened(true);
                return true;
            }
            return prev;
        });
    }, []);

    return (
        <div className={'side-nav-inner-toolbar'}>
            <Drawer
                key={`drawer-${menuMode}-${minMenuSize}`}
                className={'drawer' + menuPatch.cssClass}
                position={'before'}
                closeOnOutsideClick={closeDrawer}
                openedStateMode={menuMode}
                revealMode={menuRevealMode}
                minSize={minMenuSize}
                maxSize={250}
                shading={shaderEnabled}
                opened={menuOpened}
                template={'menu'}
            >
                <div className={'container'}>
                    <Header
                        userMenuItems={userMenuItems}
                        menuToggleEnabled={minMenuSize === 0}
                        toggleMenu={toggleMenu} />

                    <ScrollView className={'layout-body with-footer'}>
                        <div className={'content'}>
                            {React.Children.map(children, item => {
                                return item.type !== Footer && item;
                            })}
                        </div>
                        <div className={'content-block'}>
                            {React.Children.map(children, item => {
                                return item.type === Footer && item;
                            })}
                        </div>
                    </ScrollView>
                </div>
                <Template name={'menu'}>
                    <SideNavigationMenu
                        items={menuItems}
                        compactMode={!menuOpened}
                        selectedItem={location.pathname}
                        className={'dx-swatch-additional'}
                        selectedItemChanged={navigationChanged}
                        openMenu={navigationClick}
                    //onMenuReady={menuPatch.onReady}
                    >
                        <Toolbar id={'navigation-header'}>
                            {
                                minMenuSize !== 0 &&
                                <Item
                                    location={'before'}
                                    cssClass={'menu-button'}
                                    widget={'dxButton'}
                                >
                                    <div className={'sbcounty-logo'} style={{ visibility: menuOpened ? 'visible' : 'hidden' }}></div>
                                    <Button icon="menu" stylingMode="text" onClick={toggleMenu} />
                                </Item>
                            }

                            <Item location={'before'} cssClass={'header-title'} text='' />
                        </Toolbar>
                    </SideNavigationMenu>
                </Template>
            </Drawer>
        </div>
    );
}

export default SideNavInnerToolbar;
